import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo, { ORIGIN } from './Seo';
import blogData from '../data/blogData';
import logoImg from '../assets/Logo.jpeg';
import '../styles/BlogPost.css';

export default function BlogPost() {
  const { slug } = useParams();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const blog = blogData.find(b => b.slug === slug);
  const currentIndex = blogData.findIndex(b => b.slug === slug);
  const nextBlog = currentIndex >= 0 ? blogData[(currentIndex + 1) % blogData.length] : null;

  // Scroll-aware nav
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top when the article changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  const navItems = [
    { name: "Home", href: "/" },
    { name: "The Game", href: "/#about-game" },
    { name: "Locations", href: "/#locations" },
    { name: "FAQs", href: "/#faqs" },
    { name: "Blog", href: "/blog" },
    { name: "The Architect", href: "/creator" }
  ];

  const renderBlock = (block, index) => {
    switch (block.type) {
      case 'heading':
        return <h2 key={index} className="post-heading">{block.text}</h2>;
      case 'quote':
        return <blockquote key={index} className="post-quote">{block.text}</blockquote>;
      case 'list':
        return (
          <ul key={index} className="post-list">
            {block.items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
      case 'paragraph':
      default:
        return <p key={index} className="post-paragraph">{block.text}</p>;
    }
  };

  return (
    <div className="page-blog-post">
      {blog && (
        <Seo
          path={`/blog/${blog.slug}`}
          title={blog.metaTitle}
          description={blog.metaDescription}
          image={typeof blog.heroImage === 'string' && blog.heroImage.startsWith('/')
            ? `${ORIGIN}${blog.heroImage}`
            : undefined}
        >
          <meta property="og:type" content="article" />
        </Seo>
      )}


      <div className="post-page">
        {/* ===== NAVIGATION ===== */}
        <nav className={`fixed top-0 left-0 right-0 z-50 blog-nav ${scrolled ? 'is-scrolled' : ''}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16 md:h-18">
              <div className="flex-shrink-0">
                <Link to="/" style={{ textDecoration: 'none' }} className="inline-flex items-center gap-2">
                  <img src={logoImg} alt="Mystery Mansion" style={{
                    height: '32px', width: 'auto', objectFit: 'contain',
                    filter: 'drop-shadow(0 0 20px rgba(201, 168, 76, 0.15))',
                    borderRadius: '4px'
                  }} />
                </Link>
              </div>

              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-2">
                  {navItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.href}
                      className={`blog-nav-link ${item.href === '/blog' ? 'active' : ''}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="md:hidden">
                <button
                  onClick={(e) => { e.stopPropagation(); setIsMenuOpen(!isMenuOpen); }}
                  className="focus:outline-none p-2"
                  aria-label="Toggle menu"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {isMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div className={`md:hidden blog-mobile-menu ${isMenuOpen ? 'open' : ''}`}>
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.href}
                    className={`blog-nav-link block py-3 ${item.href === '/blog' ? 'active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        {!blog ? (
          /* ===== ARTICLE NOT FOUND ===== */
          <div className="post-notfound">
            <h1>This mystery has no ending</h1>
            <p>The article you're looking for doesn't exist or may have been moved. Head back to the archives to keep reading.</p>
            <Link to="/blog" className="post-notfound-btn">Back to Blog</Link>
          </div>
        ) : (
          <>
            <article className="post-article">
              <div className="post-hero">
                <Link to="/blog" className="post-back">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  Back to Blog
                </Link>
                <div>
                  <span className="post-category">{blog.category}</span>
                </div>
                <h1 className="post-title">{blog.title}</h1>
                <p className="post-subtitle">{blog.subtitle}</p>
                <div className="post-meta">
                  <span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {blog.date}
                  </span>
                  <span className="post-readtime">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {blog.readTime}
                  </span>
                </div>
              </div>

              <img src={blog.heroImage} alt={blog.heroAlt} className="post-hero-img" />

              {blog.content.map((block, index) => renderBlock(block, index))}
            </article>

            {nextBlog && nextBlog.slug !== blog.slug && (
              <div className="post-next">
                <div className="post-next-label">Next Story</div>
                <Link to={`/blog/${nextBlog.slug}`} className="post-next-link">
                  {nextBlog.title} →
                </Link>
              </div>
            )}
          </>
        )}

        {/* ===== FOOTER ===== */}
        <footer className="blog-footer">
          <p className="blog-footer-text">
            © {new Date().getFullYear()} Mystery Mansion. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}
